<template>
    <section class="student-certificates">
        <div class="container student-certificates__container">
            <StudentSidebar :student="student" />

            <main class="student-certificates__main">
                <header class="student-certificates__topbar">
                    <div>
                        <span class="student-certificates__eyebrow">Achievements</span>
                        <h1>My Certificate</h1>
                        <p>Track your course certificates and download them after full course completion.</p>
                    </div>

                    <a href="/student/enrolled-courses" class="student-certificates__back">
                        <i class="ri-arrow-left-line"></i>
                        Enrolled Courses
                    </a>
                </header>

                <section class="student-certificates__stats" aria-label="Certificate summary">
                    <article class="student-certificates__stat is-ready">
                        <i class="ri-award-line"></i>
                        <div>
                            <strong>{{ readyCount }}</strong>
                            <span>Ready</span>
                        </div>
                    </article>

                    <article class="student-certificates__stat is-locked">
                        <i class="ri-lock-line"></i>
                        <div>
                            <strong>{{ lockedCount }}</strong>
                            <span>Locked</span>
                        </div>
                    </article>

                    <article class="student-certificates__stat is-total">
                        <i class="ri-book-open-line"></i>
                        <div>
                            <strong>{{ certificates.length }}</strong>
                            <span>Courses</span>
                        </div>
                    </article>
                </section>

                <section class="student-certificates__panel">
                    <div class="student-certificates__panel-head">
                        <div>
                            <span class="student-certificates__eyebrow">Certificates</span>
                            <h2>Course Certificates</h2>
                        </div>
                    </div>

                    <div v-if="certificates.length" class="student-certificates__grid">
                        <article
                            v-for="item in certificates"
                            :key="item.id"
                            class="student-certificates__card"
                            :class="`is-${item.statusTone}`"
                        >
                            <div class="student-certificates__media">
                                <img :src="item.thumbnailPath || defaultCourseImage" alt="" />
                                <span class="student-certificates__badge" :class="`is-${item.statusTone}`">
                                    <i :class="item.isUnlocked ? 'ri-checkbox-circle-line' : (item.isExpired ? 'ri-time-line' : 'ri-lock-line')"></i>
                                    {{ item.statusLabel }}
                                </span>
                            </div>

                            <div class="student-certificates__body">
                                <div class="student-certificates__title-row">
                                    <h3>{{ item.title }}</h3>
                                    <span>{{ Math.round(Number(item.progress || 0)) }}%</span>
                                </div>

                                <div
                                    class="student-certificates__progress"
                                    :aria-label="`${item.title}: ${item.progress}% completed`"
                                >
                                    <span :style="{ width: `${item.progress || 0}%` }"></span>
                                </div>

                                <p>{{ item.lockReason }}</p>

                                <div class="student-certificates__actions">
                                    <a
                                        v-if="item.downloadUrl"
                                        :href="item.downloadUrl"
                                        class="student-certificates__download"
                                    >
                                        <i class="ri-download-2-line"></i>
                                        Download Certificate
                                    </a>

                                    <button
                                        v-else-if="item.generateUrl"
                                        type="button"
                                        class="student-certificates__download"
                                        :disabled="isGenerating(item.id)"
                                        @click="generateCertificate(item)"
                                    >
                                        <i class="ri-award-line"></i>
                                        {{ isGenerating(item.id) ? 'Generating...' : 'Generate Certificate' }}
                                    </button>

                                    <button
                                        v-else-if="item.isExpired"
                                        type="button"
                                        class="student-certificates__expired-action"
                                        @click="handleExpiredCertificate(item)"
                                    >
                                        <i class="ri-time-line"></i>
                                        Course Expired
                                    </button>

                                    <button
                                        v-else-if="isTemplatePending(item)"
                                        type="button"
                                        class="student-certificates__waiting-action"
                                        disabled
                                    >
                                        <i class="ri-time-line"></i>
                                        Wait for Template
                                    </button>

                                    <a
                                        v-else
                                        :href="item.detailUrl || '/student/enrolled-courses'"
                                        class="student-certificates__continue"
                                    >
                                        <i class="ri-play-circle-line"></i>
                                        Continue Course
                                    </a>
                                </div>
                            </div>
                        </article>
                    </div>

                    <div v-else class="student-certificates__empty">
                        <i class="ri-award-line"></i>
                        <strong>No certificates found.</strong>
                        <span>Your course certificates will appear here after enrollment.</span>
                        <a href="/online-training">Browse Courses</a>
                    </div>
                </section>
            </main>
        </div>
    </section>
</template>

<script setup>
import { computed, ref } from 'vue';
import StudentSidebar from '@/components/website/student/StudentSidebar.vue';

const root = document.getElementById('studentCertificatesComponent');
const defaultCourseImage = '/media/default/default-image.jpg';

const parseJson = (value, fallback = {}) => {
    try {
        return value ? JSON.parse(value) : fallback;
    } catch (error) {
        return fallback;
    }
};

const student = parseJson(root?.dataset.student);
const certificates = ref(parseJson(root?.dataset.certificates, []));
const generatingIds = ref([]);

const readyCount = computed(() => certificates.value.filter(item => item.isUnlocked).length);
const lockedCount = computed(() => certificates.value.filter(item => !item.isUnlocked).length);
const isTemplatePending = (item) => {
    return Number(item?.progress || 0) >= 100
        && item?.certificateAvailable
        && !item?.templateAvailable
        && !item?.isExpired;
};

const notify = (message, type = 'success') => {
    if (typeof window.$toast === 'function') {
        window.$toast(message, type);
    }
};

const isGenerating = (courseId) => generatingIds.value.includes(courseId);

const generateCertificate = async (item) => {
    if (!item?.generateUrl || isGenerating(item.id)) {
        return;
    }

    generatingIds.value.push(item.id);

    try {
        const { data } = await axios.post(item.generateUrl);

        item.hasGeneratedCertificate = true;
        item.downloadUrl = data?.download_url || item.downloadUrl;
        item.lockReason = 'Certificate is ready to download.';
        // notify(data?.message || 'Certificate generated successfully.', data?.type || 'success');
        $toast(data?.message || 'Certificate generated successfully.', data?.type || 'success');
    } catch (error) {
        const message = error.response?.data?.message || 'Something went wrong while generating the certificate.';
        $toast(message, 'error');
    } finally {
        generatingIds.value = generatingIds.value.filter(id => id !== item.id);
    }
};

const handleExpiredCertificate = (item) => {
    const message = item.lockReason || 'Your access to this course has expired. Please enroll again to continue.';

    if (typeof Swal !== 'undefined') {
        Swal.fire({
            icon: 'warning',
            title: 'Course Expired',
            text: message,
            confirmButtonText: 'Re-enroll Now',
            confirmButtonColor: '#0d6efd',
        }).then((result) => {
            if (result.isConfirmed) {
                location.href = item.purchaseUrl || '/online-training';
            }
        });

        return;
    }

    alert(message);
};
</script>

<style scoped>
.student-certificates {
    background:
        radial-gradient(circle at top left, rgba(13, 110, 253, 0.1), transparent 32%),
        linear-gradient(180deg, #f5f8fc 0%, #edf1f6 52%, #f8fafc 100%);
    padding: 32px 0 46px;
}

.student-certificates__container {
    display: grid;
    grid-template-columns: 240px minmax(0, 1fr);
    gap: 24px;
    width: 100%;
    align-items: start;
}

.student-certificates__main {
    min-width: 0;
}

.student-certificates__topbar,
.student-certificates__stat,
.student-certificates__panel {
    background: #ffffff;
    border: 1px solid rgba(226, 232, 240, 0.92);
    border-radius: 8px;
    box-shadow: 0 18px 42px rgba(15, 23, 42, 0.06);
}

.student-certificates__topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 18px 22px;
    margin-bottom: 18px;
    background:
        linear-gradient(135deg, rgba(13, 110, 253, 0.08), rgba(32, 201, 151, 0.06)),
        #ffffff;
    border-color: #dcecff;
}

.student-certificates__topbar h1,
.student-certificates__panel-head h2,
.student-certificates__card h3 {
    margin: 0;
    color: #111827;
    font-weight: 800;
}

.student-certificates__topbar h1 {
    font-size: 24px;
}

.student-certificates__topbar p {
    max-width: 620px;
    margin: 6px 0 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.55;
}

.student-certificates__eyebrow {
    display: block;
    margin-bottom: 4px;
    color: #0d6efd;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
}

.student-certificates__back,
.student-certificates__download,
.student-certificates__expired-action,
.student-certificates__waiting-action,
.student-certificates__continue,
.student-certificates__empty a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 40px;
    padding: 9px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 800;
    text-decoration: none;
    transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.student-certificates__back,
.student-certificates__continue,
.student-certificates__empty a {
    background: #e7f1ff;
    color: #0d6efd;
}

.student-certificates__download {
    background: #ecfdf5;
    color: #047857;
}

.student-certificates__expired-action {
    border: 0;
    background: #fff1f2;
    color: #dc2626;
    cursor: pointer;
}

.student-certificates__waiting-action {
    border: 0;
    background: #f8fafc;
    color: #64748b;
    cursor: not-allowed;
}

.student-certificates__back:hover,
.student-certificates__continue:hover,
.student-certificates__empty a:hover {
    background: #dbeafe;
    color: #0b5ed7;
    transform: translateY(-1px);
}

.student-certificates__download:hover {
    background: #d1fae5;
    color: #065f46;
    transform: translateY(-1px);
}

.student-certificates__expired-action:hover {
    background: #ffe4e6;
    color: #b91c1c;
    transform: translateY(-1px);
}

.student-certificates__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
    margin-bottom: 18px;
}

.student-certificates__stat {
    display: flex;
    align-items: center;
    gap: 14px;
    min-height: 96px;
    padding: 18px;
}

.student-certificates__stat i {
    display: grid;
    place-items: center;
    flex: 0 0 44px;
    width: 44px;
    height: 44px;
    border-radius: 8px;
    font-size: 23px;
}

.student-certificates__stat strong,
.student-certificates__stat span {
    display: block;
}

.student-certificates__stat strong {
    color: #111827;
    font-size: 28px;
    line-height: 1;
}

.student-certificates__stat span {
    margin-top: 6px;
    color: #64748b;
    font-size: 12px;
    font-weight: 800;
}

.student-certificates__stat.is-ready i {
    background: #ecfdf5;
    color: #047857;
}

.student-certificates__stat.is-locked i {
    background: #fff7ed;
    color: #c2410c;
}

.student-certificates__stat.is-total i {
    background: #eff6ff;
    color: #2563eb;
}

.student-certificates__panel {
    position: relative;
    overflow: hidden;
    padding: 22px;
}

.student-certificates__panel::before {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    height: 4px;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 56%, #7c3aed 100%);
    content: "";
}

.student-certificates__panel-head {
    margin-bottom: 18px;
}

.student-certificates__panel-head h2 {
    font-size: 19px;
}

.student-certificates__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}

.student-certificates__card {
    overflow: hidden;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: #ffffff;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.student-certificates__card:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 34px rgba(15, 23, 42, 0.08);
}

.student-certificates__media {
    position: relative;
    aspect-ratio: 16 / 7;
    overflow: hidden;
    background: #f1f5f9;
}

.student-certificates__media img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.student-certificates__badge {
    position: absolute;
    top: 12px;
    left: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 30px;
    padding: 6px 10px;
    border-radius: 999px;
    background: #ffffff;
    color: #475569;
    font-size: 12px;
    font-weight: 900;
    box-shadow: 0 10px 24px rgba(15, 23, 42, 0.12);
}

.student-certificates__badge.is-ready {
    color: #047857;
}

.student-certificates__badge.is-locked {
    color: #c2410c;
}

.student-certificates__badge.is-unavailable {
    color: #64748b;
}

.student-certificates__badge.is-expired {
    color: #dc2626;
}

.student-certificates__body {
    display: grid;
    gap: 12px;
    padding: 16px;
}

.student-certificates__title-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 12px;
    align-items: start;
}

.student-certificates__card h3 {
    font-size: 16px;
    line-height: 1.35;
}

.student-certificates__title-row > span {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 50px;
    min-height: 30px;
    padding: 5px 8px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #334155;
    font-size: 12px;
    font-weight: 900;
}

.student-certificates__progress {
    height: 8px;
    overflow: hidden;
    border-radius: 999px;
    background: #e2e8f0;
}

.student-certificates__progress span {
    display: block;
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #0d6efd 0%, #20c997 100%);
}

.student-certificates__body p {
    min-height: 40px;
    margin: 0;
    color: #64748b;
    font-size: 13px;
    line-height: 1.55;
}

.student-certificates__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.student-certificates__empty {
    display: grid;
    justify-items: center;
    gap: 9px;
    min-height: 260px;
    padding: 36px 16px;
    border: 1px dashed #cbd5e1;
    border-radius: 8px;
    background: #f8fbff;
    color: #64748b;
    text-align: center;
}

.student-certificates__empty > i {
    display: grid;
    place-items: center;
    width: 58px;
    height: 58px;
    border-radius: 50%;
    background: #e7f1ff;
    color: #0d6efd;
    font-size: 28px;
}

.student-certificates__empty strong {
    color: #111827;
    font-size: 16px;
    font-weight: 900;
}

.student-certificates__empty span {
    font-size: 13px;
    font-weight: 600;
}

@media (max-width: 1199px) {
    .student-certificates__container {
        grid-template-columns: 210px minmax(0, 1fr);
    }
}

@media (max-width: 991px) {
    .student-certificates {
        padding: 24px 0 36px;
    }

    .student-certificates__container {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 767px) {
    .student-certificates__topbar {
        flex-direction: column;
        align-items: flex-start;
    }

    .student-certificates__stats,
    .student-certificates__grid {
        grid-template-columns: 1fr;
    }

    .student-certificates__back,
    .student-certificates__download,
    .student-certificates__continue {
        width: 100%;
    }
}

@media (max-width: 575px) {
    .student-certificates__container {
        padding: 0 10px;
    }

    .student-certificates__topbar,
    .student-certificates__panel {
        padding: 16px;
    }
}
</style>
